import { gql } from '@apollo/client';

const CONVERSATION_MESSAGE_FRAGMENT = gql`
    fragment ConversationMessageFields on AgentConversationMessageType {
        id
        role
        content
        status
        toolCalls
        createdAt
        scheduleName
        scheduleRunStatus
        scheduleRunError
        scheduleTestRun
        schedule {
            id
        }
        attachments {
            id
            source
            fileName
            contentType
            downloadUrl
            previewUrl
        }
    }
`;

const CONVERSATION_SCHEDULE_FRAGMENT = gql`
    fragment ConversationScheduleFields on AgentConversationScheduleType {
        id
        name
        prompt
        frequency
        timeOfDay
        weekday
        intervalMinutes
        selectedTools
        active
        nextRunAt
        lastRunStartedAt
        lastRunAt
        lastRunStatus
        conversationId
        conversationTitle
        dayOfMonth
        useLastDayIfMissing
        intervalUnit
        timezone
        endsOn
        isEnded
        deliverToConversation
        emailRecipients
        exportFormat
        lastRunError
        consecutiveFailures
        autoPaused
        notifyOnRun
        createdAt
        recipientTeam {
            id
            name
        }
        recipients {
            id
            firstName
            lastName
            email
        }
    }
`;

export const GET_CONVERSATIONS = gql`
    query AgentConversations {
        agentConversations {
            id
            title
            pinned
            createdAt
            updatedAt
            # support and internal users see every chat of the customer; the owner is shown on chats
            # that are not theirs and the chat actions are hidden there
            isOwner
            createdBy {
                id
                firstName
                lastName
                email
            }
            project {
                id
                agent {
                    id
                    name
                }
            }
            # who the owner shared the chat with, for the sidebar's "Shared with 2" (ENG-11506)
            shares {
                id
                team {
                    id
                }
                members {
                    id
                }
            }
            # set on a chat receiving someone's scheduled runs (ENG-11387): listed under "Shared with me"
            scheduledDelivery {
                id
                name
                sender {
                    id
                    firstName
                    lastName
                    email
                }
            }
        }
    }
`;

export const GET_SEARCH_RESULTS_PAGE = gql`
    query AgentConversationSearchResultsPage($messageId: UUID!, $toolCallIndex: Int!, $page: Int!, $pageSize: Int!) {
        agentConversationSearchResultsPage(
            messageId: $messageId
            toolCallIndex: $toolCallIndex
            page: $page
            pageSize: $pageSize
        )
    }
`;

// a chat handed to people as a frozen snapshot (ENG-11386): who shared it, with whom, and the snapshot itself
const CONVERSATION_SHARE_FRAGMENT = gql`
    fragment ConversationShareFields on AgentConversationShareType {
        id
        createdAt
        note
        sharedBy {
            id
            firstName
            lastName
            email
        }
        team {
            id
            name
            users {
                id
            }
        }
        members {
            id
            firstName
            lastName
            email
        }
        sourceMessage {
            id
        }
        snapshot {
            id
            title
            createdAt
        }
    }
`;

// a schedule someone else sends to the viewer (ENG-11387): read-only, never its tools, addresses or other recipients
const SHARED_SCHEDULE_FRAGMENT = gql`
    fragment SharedScheduleFields on SharedScheduleType {
        id
        name
        prompt
        frequency
        timeOfDay
        weekday
        dayOfMonth
        useLastDayIfMissing
        intervalMinutes
        intervalUnit
        timezone
        endsOn
        active
        nextRunAt
        lastRunAt
        isMuted
        conversationId
        sender {
            id
            firstName
            lastName
            email
        }
    }
`;

export const GET_CONVERSATION = gql`
    ${SHARED_SCHEDULE_FRAGMENT}
    ${CONVERSATION_MESSAGE_FRAGMENT}
    ${CONVERSATION_SHARE_FRAGMENT}
    query AgentConversation($id: UUID!) {
        agentConversation(id: $id) {
            id
            title
            pinned
            modelKey
            toolsEnabled
            selectedTools
            contextTokens
            contextWindow
            userRatingHelpful
            isOwner
            createdBy {
                id
                firstName
                lastName
                email
            }
            share {
                ...ConversationShareFields
            }
            shares {
                ...ConversationShareFields
            }
            continuedFrom {
                id
                title
                createdBy {
                    id
                    firstName
                    lastName
                    email
                }
            }
            scheduledDelivery {
                ...SharedScheduleFields
            }
            project {
                id
                name
                agent {
                    id
                    name
                }
            }
            attachments {
                id
                fileName
                contentType
                downloadUrl
                previewUrl
            }
            messages {
                ...ConversationMessageFields
            }
        }
    }
`;

// snapshots other people shared with the viewer, newest first (ENG-11386)
export const GET_SHARED_CONVERSATIONS = gql`
    ${CONVERSATION_SHARE_FRAGMENT}
    query SharedAgentConversations {
        sharedAgentConversations {
            ...ConversationShareFields
        }
    }
`;

export const SHARE_CONVERSATION = gql`
    ${CONVERSATION_SHARE_FRAGMENT}
    mutation ShareAgentConversation($id: UUID!, $teamId: UUID, $memberIds: [ID!], $messageId: UUID, $note: String) {
        shareAgentConversation(id: $id, teamId: $teamId, memberIds: $memberIds, messageId: $messageId, note: $note) {
            share {
                ...ConversationShareFields
            }
        }
    }
`;

export const UPDATE_CONVERSATION_SHARE = gql`
    ${CONVERSATION_SHARE_FRAGMENT}
    mutation UpdateAgentConversationShare($id: UUID!, $teamId: UUID, $memberIds: [ID!]) {
        updateAgentConversationShare(id: $id, teamId: $teamId, memberIds: $memberIds) {
            revoked
            share {
                ...ConversationShareFields
            }
        }
    }
`;

export const CONTINUE_SHARED_CONVERSATION = gql`
    mutation ContinueSharedAgentConversation($id: UUID!, $messageId: UUID) {
        continueSharedAgentConversation(id: $id, messageId: $messageId) {
            conversation {
                id
            }
        }
    }
`;

export const GET_CHAT_AGENTS = gql`
    query ChatAgents {
        agents {
            id
            name
            enabled
        }
    }
`;

// no project = workspace chat (ENG-10934): the backend derives the toolset from the user; a project backed by
// an inbox agent resolves to that agent's tools (ENG-11385)
export const GET_CHAT_TOOLS = gql`
    query ChatTools($projectId: UUID) {
        chatTools(projectId: $projectId) {
            name
            description
        }
    }
`;

export const GET_AVAILABLE_CHAT_MODELS = gql`
    query AvailableChatModels {
        availableChatModels {
            key
            label
        }
    }
`;

// kept apart from GET_VIEWER so it can be refetched after every turn without reloading the whole viewer
export const GET_CHAT_USAGE = gql`
    query ChatUsage {
        viewer {
            id
            usedMonthlyChatLlmCostPercent
        }
    }
`;

export const CREATE_CONVERSATION = gql`
    mutation CreateAgentConversation(
        $projectId: UUID
        $modelKey: String
        $toolsEnabled: Boolean
        $selectedTools: [String]
        $entryPoint: String
        $promptId: String
        $promptCategory: String
    ) {
        createAgentConversation(
            projectId: $projectId
            modelKey: $modelKey
            toolsEnabled: $toolsEnabled
            selectedTools: $selectedTools
            entryPoint: $entryPoint
            promptId: $promptId
            promptCategory: $promptCategory
        ) {
            conversation {
                id
            }
        }
    }
`;

export const SEND_CONVERSATION_MESSAGE = gql`
    ${CONVERSATION_MESSAGE_FRAGMENT}
    mutation SendAgentConversationMessage($conversationId: UUID!, $message: String!, $attachmentIds: [UUID]) {
        sendAgentConversationMessage(
            conversationId: $conversationId
            message: $message
            attachmentIds: $attachmentIds
        ) {
            conversation {
                id
                title
                updatedAt
                contextTokens
                contextWindow
                userRatingHelpful
                attachments {
                    id
                    fileName
                    contentType
                    downloadUrl
                    previewUrl
                }
                messages {
                    ...ConversationMessageFields
                }
            }
        }
    }
`;

export const EDIT_CONVERSATION_MESSAGE = gql`
    ${CONVERSATION_MESSAGE_FRAGMENT}
    mutation EditAgentConversationMessage($conversationId: UUID!, $messageId: UUID!, $message: String!) {
        editAgentConversationMessage(conversationId: $conversationId, messageId: $messageId, message: $message) {
            conversation {
                id
                title
                updatedAt
                contextTokens
                contextWindow
                userRatingHelpful
                attachments {
                    id
                    fileName
                    contentType
                    downloadUrl
                    previewUrl
                }
                messages {
                    ...ConversationMessageFields
                }
            }
        }
    }
`;

export const UPLOAD_CONVERSATION_ATTACHMENT = gql`
    mutation UploadAgentConversationAttachment($conversationId: UUID!, $file: Upload!, $messageId: UUID) {
        uploadAgentConversationAttachment(conversationId: $conversationId, file: $file, messageId: $messageId) {
            attachment {
                id
                source
                fileName
                contentType
                readable
                downloadUrl
                previewUrl
            }
        }
    }
`;

export const SET_CONVERSATION_PINNED = gql`
    mutation SetAgentConversationPinned($id: UUID!, $pinned: Boolean!) {
        setAgentConversationPinned(id: $id, pinned: $pinned) {
            conversation {
                id
                pinned
            }
        }
    }
`;

export const RATE_CONVERSATION = gql`
    mutation RateAgentConversation($id: UUID!, $helpful: Boolean) {
        rateAgentConversation(id: $id, helpful: $helpful) {
            conversation {
                id
                userRatingHelpful
            }
        }
    }
`;

export const DELETE_CONVERSATION = gql`
    mutation DeleteAgentConversation($id: UUID!) {
        deleteAgentConversation(id: $id) {
            success
        }
    }
`;

export const GET_CONVERSATION_SCHEDULES = gql`
    ${CONVERSATION_SCHEDULE_FRAGMENT}
    query AgentConversationSchedules {
        agentConversationSchedules {
            ...ConversationScheduleFields
        }
    }
`;

// a short name summarized from the prompt; null when the backend can't suggest one (chat disabled, budget used up, LLM error)
export const SUGGEST_SCHEDULE_NAME = gql`
    mutation SuggestScheduleName($prompt: String!) {
        suggestAgentConversationScheduleName(prompt: $prompt) {
            name
        }
    }
`;

export const CREATE_CONVERSATION_SCHEDULE = gql`
    ${CONVERSATION_SCHEDULE_FRAGMENT}
    mutation CreateAgentConversationSchedule(
        $conversationId: UUID!
        $name: String!
        $prompt: String!
        $frequency: String!
        $timeOfDay: Time
        $weekday: Int
        $dayOfMonth: Int
        $useLastDayIfMissing: Boolean
        $intervalMinutes: Int
        $intervalUnit: String
        $timezone: String
        $endsOn: Date
        $deliverToConversation: Boolean
        $emailRecipients: [String!]
        $exportFormat: String
        $notifyOnRun: Boolean
        $selectedTools: [String]
        $active: Boolean
        $recipients: ScheduleRecipientsInput
    ) {
        createAgentConversationSchedule(
            conversationId: $conversationId
            name: $name
            prompt: $prompt
            frequency: $frequency
            timeOfDay: $timeOfDay
            weekday: $weekday
            dayOfMonth: $dayOfMonth
            useLastDayIfMissing: $useLastDayIfMissing
            intervalMinutes: $intervalMinutes
            intervalUnit: $intervalUnit
            timezone: $timezone
            endsOn: $endsOn
            deliverToConversation: $deliverToConversation
            emailRecipients: $emailRecipients
            exportFormat: $exportFormat
            notifyOnRun: $notifyOnRun
            selectedTools: $selectedTools
            active: $active
            recipients: $recipients
        ) {
            schedule {
                ...ConversationScheduleFields
            }
        }
    }
`;

export const UPDATE_CONVERSATION_SCHEDULE = gql`
    ${CONVERSATION_SCHEDULE_FRAGMENT}
    mutation UpdateAgentConversationSchedule(
        $id: UUID!
        $name: String
        $prompt: String
        $frequency: String
        $timeOfDay: Time
        $weekday: Int
        $dayOfMonth: Int
        $useLastDayIfMissing: Boolean
        $intervalMinutes: Int
        $intervalUnit: String
        $timezone: String
        $endsOn: Date
        $deliverToConversation: Boolean
        $emailRecipients: [String!]
        $exportFormat: String
        $notifyOnRun: Boolean
        $selectedTools: [String]
        $active: Boolean
        $recipients: ScheduleRecipientsInput
    ) {
        updateAgentConversationSchedule(
            id: $id
            name: $name
            prompt: $prompt
            frequency: $frequency
            timeOfDay: $timeOfDay
            weekday: $weekday
            dayOfMonth: $dayOfMonth
            useLastDayIfMissing: $useLastDayIfMissing
            intervalMinutes: $intervalMinutes
            intervalUnit: $intervalUnit
            timezone: $timezone
            endsOn: $endsOn
            deliverToConversation: $deliverToConversation
            emailRecipients: $emailRecipients
            exportFormat: $exportFormat
            notifyOnRun: $notifyOnRun
            selectedTools: $selectedTools
            active: $active
            recipients: $recipients
        ) {
            schedule {
                ...ConversationScheduleFields
            }
        }
    }
`;

export const DELETE_CONVERSATION_SCHEDULE = gql`
    mutation DeleteAgentConversationSchedule($id: UUID!) {
        deleteAgentConversationSchedule(id: $id) {
            success
        }
    }
`;

export const GET_CONVERSATION_SCHEDULE = gql`
    ${CONVERSATION_SCHEDULE_FRAGMENT}
    query AgentConversationSchedule($id: UUID!, $runsLimit: Int = 10, $runsOffset: Int = 0, $runsStatus: String) {
        agentConversationSchedule(id: $id) {
            ...ConversationScheduleFields
            runsCount(status: $runsStatus)
            runs(limit: $runsLimit, offset: $runsOffset, status: $runsStatus) {
                id
                createdAt
                status
                errorCode
                isTest
                messageId
            }
        }
    }
`;

export const RUN_SCHEDULE_NOW = gql`
    ${CONVERSATION_SCHEDULE_FRAGMENT}
    mutation RunAgentConversationScheduleNow($id: UUID!, $test: Boolean, $resumeOnSuccess: Boolean) {
        runAgentConversationScheduleNow(id: $id, test: $test, resumeOnSuccess: $resumeOnSuccess) {
            schedule {
                ...ConversationScheduleFields
            }
        }
    }
`;

const TRIGGER_FRAGMENT = gql`
    fragment TriggerFields on TriggerType {
        id
        name
        channelId
        documentType
        senders
        instruction
        active
        lastFiredAt
        lastStatus
        createdAt
        projectId
        projectName
        conversationId
    }
`;

export const GET_AGENT_TRIGGERS = gql`
    ${TRIGGER_FRAGMENT}
    query AgentTriggers($agentId: UUID!) {
        agentTriggers(agentId: $agentId) {
            ...TriggerFields
        }
    }
`;

export const GET_MY_TRIGGERS = gql`
    ${TRIGGER_FRAGMENT}
    query MyTriggers {
        myTriggers {
            ...TriggerFields
            agentId
            agentName
            channelName
        }
    }
`;

export const GET_AGENT_TRIGGER_CHANNELS = gql`
    query AgentTriggerChannels($agentId: UUID) {
        agentTriggerChannels(agentId: $agentId) {
            id
            name
            documentType
        }
    }
`;

export const GET_TRIGGER_OPTIONS = gql`
    query TriggerOptions {
        triggerOptions {
            eventTypes {
                value
                label
            }
            conditionTypes {
                value
                label
            }
        }
    }
`;

export const CONFIRM_AGENT_TOOL_CALL = gql`
    ${CONVERSATION_MESSAGE_FRAGMENT}
    mutation ConfirmAgentToolCall($messageId: UUID!, $payload: String) {
        confirmAgentToolCall(messageId: $messageId, payload: $payload) {
            message {
                ...ConversationMessageFields
            }
            conversation {
                id
                attachments {
                    id
                    fileName
                    contentType
                    downloadUrl
                    previewUrl
                }
            }
        }
    }
`;

export const CREATE_TRIGGER = gql`
    ${TRIGGER_FRAGMENT}
    mutation CreateTrigger(
        $agentId: UUID
        $projectId: UUID
        $channelId: UUID!
        $documentType: String!
        $senders: [String!]!
        $name: String!
        $instruction: String!
    ) {
        createTrigger(
            agentId: $agentId
            projectId: $projectId
            channelId: $channelId
            documentType: $documentType
            senders: $senders
            name: $name
            instruction: $instruction
        ) {
            trigger {
                ...TriggerFields
            }
        }
    }
`;

export const CANCEL_AGENT_TOOL_CALL = gql`
    ${CONVERSATION_MESSAGE_FRAGMENT}
    mutation CancelAgentToolCall($messageId: UUID!) {
        cancelAgentToolCall(messageId: $messageId) {
            message {
                ...ConversationMessageFields
            }
        }
    }
`;

export const UPDATE_TRIGGER = gql`
    ${TRIGGER_FRAGMENT}
    mutation UpdateTrigger(
        $id: UUID!
        $channelId: UUID
        $documentType: String
        $senders: [String!]
        $name: String
        $instruction: String
        $active: Boolean
        $projectId: UUID
    ) {
        updateTrigger(
            id: $id
            channelId: $channelId
            documentType: $documentType
            senders: $senders
            name: $name
            instruction: $instruction
            active: $active
            projectId: $projectId
        ) {
            trigger {
                ...TriggerFields
            }
        }
    }
`;

export const DELETE_TRIGGER = gql`
    mutation DeleteTrigger($id: UUID!) {
        deleteTrigger(id: $id) {
            success
        }
    }
`;

// ---------------------------------------------------------------- shared projects (ENG-11385)

// what the projects grid and the chat picker need; the detail page loads ProjectFields
const PROJECT_LIST_FRAGMENT = gql`
    fragment ProjectListFields on ProjectType {
        id
        name
        canManage
        agent {
            id
            name
            enabled
        }
        team {
            id
            name
        }
        owner {
            id
            firstName
            lastName
            email
        }
        members {
            id
        }
        knowledgeFiles {
            id
            sourceType
        }
        knowledgeUrls
        sharepointKnowledgeSource {
            integrationId
        }
        sftpKnowledgeSource {
            sftpConfigId
        }
    }
`;

const PROJECT_FRAGMENT = gql`
    fragment ProjectFields on ProjectType {
        id
        name
        description
        canManage
        createdAt
        updatedAt
        instructions {
            label
            body
        }
        owner {
            id
            firstName
            lastName
            email
        }
        team {
            id
            name
        }
        members {
            id
            firstName
            lastName
            email
        }
        agent {
            id
            name
            enabled
        }
        sharepointKnowledgeSource {
            integrationId
            integrationName
            siteId
            siteName
            driveId
            driveName
            selectedItems {
                id
                path
            }
            syncStatus
            syncError
            lastSyncedAt
        }
        sftpKnowledgeSource {
            sftpConfigId
            path
            syncStatus
            syncError
            lastSyncedAt
        }
        knowledgeUrls
        knowledgeUrlStatuses {
            url
            status
            error
            syncedAt
        }
        erpToolProvider {
            key
            integrationId
        }
        orderHistoryToolProvider {
            enabled
        }
        attachmentsEnabled
        defaultChannels {
            orderChannelId
            orderConfirmationChannelId
            rfqChannelId
            invoiceChannelId
            deliveryNoteChannelId
        }
        knowledgeFiles {
            id
            fileName
            fileUrl
            createdAt
            isSynced
            sourceType
            sourceItemId
            sourceSyncedAt
            createdBy {
                id
                firstName
                lastName
                email
            }
        }
    }
`;

export const GET_PROJECTS = gql`
    ${PROJECT_LIST_FRAGMENT}
    query Projects {
        projects {
            ...ProjectListFields
        }
    }
`;

export const GET_PROJECT = gql`
    ${PROJECT_FRAGMENT}
    query Project($id: UUID!) {
        project(id: $id) {
            ...ProjectFields
        }
    }
`;

export const GET_SHARED_SCHEDULES = gql`
    ${SHARED_SCHEDULE_FRAGMENT}
    query SharedSchedules {
        sharedSchedules {
            ...SharedScheduleFields
        }
    }
`;

// whether "email me the result" can be offered: it sends from the user's own connected mailbox
export const GET_SCHEDULE_DELIVERY_OPTIONS = gql`
    query ScheduleDeliveryOptions {
        scheduleDeliveryOptions {
            canSendEmail
            mailboxAddress
            mailboxConnected
        }
    }
`;

export const SET_SHARED_SCHEDULE_MUTED = gql`
    ${SHARED_SCHEDULE_FRAGMENT}
    mutation SetSharedScheduleMuted($id: UUID!, $muted: Boolean!) {
        setSharedScheduleMuted(id: $id, muted: $muted) {
            schedule {
                ...SharedScheduleFields
            }
        }
    }
`;

export const GET_PROJECT_SHARE_OPTIONS = gql`
    query ProjectShareOptions {
        projectShareOptions {
            teams {
                id
                name
            }
            users {
                id
                firstName
                lastName
                email
            }
        }
    }
`;

export const CREATE_PROJECT = gql`
    ${PROJECT_FRAGMENT}
    mutation CreateProject(
        $name: String!
        $description: String
        $instructions: [PromptSectionInput!]
        $sharepointKnowledgeSource: SharepointToolProviderInput
        $sftpKnowledgeSource: SftpKnowledgeSourceInput
        $knowledgeUrls: [String!]
    ) {
        createProject(
            name: $name
            description: $description
            instructions: $instructions
            sharepointKnowledgeSource: $sharepointKnowledgeSource
            sftpKnowledgeSource: $sftpKnowledgeSource
            knowledgeUrls: $knowledgeUrls
        ) {
            project {
                ...ProjectFields
            }
        }
    }
`;

export const UPDATE_PROJECT = gql`
    ${PROJECT_FRAGMENT}
    mutation UpdateProject(
        $id: UUID!
        $name: String
        $description: String
        $instructions: [PromptSectionInput!]
        $sharepointKnowledgeSource: SharepointToolProviderInput
        $sftpKnowledgeSource: SftpKnowledgeSourceInput
        $knowledgeUrls: [String!]
        $erpToolProvider: ErpToolProviderInput
        $orderHistoryToolProvider: OrderHistoryToolProviderInput
        $attachmentsEnabled: Boolean
        $defaultChannels: DefaultChannelsInput
    ) {
        updateProject(
            id: $id
            name: $name
            description: $description
            instructions: $instructions
            sharepointKnowledgeSource: $sharepointKnowledgeSource
            sftpKnowledgeSource: $sftpKnowledgeSource
            knowledgeUrls: $knowledgeUrls
            erpToolProvider: $erpToolProvider
            orderHistoryToolProvider: $orderHistoryToolProvider
            attachmentsEnabled: $attachmentsEnabled
            defaultChannels: $defaultChannels
        ) {
            project {
                ...ProjectFields
            }
        }
    }
`;

export const SHARE_PROJECT = gql`
    ${PROJECT_FRAGMENT}
    mutation ShareProject($id: UUID!, $teamId: UUID, $memberIds: [ID!]) {
        shareProject(id: $id, teamId: $teamId, memberIds: $memberIds) {
            project {
                ...ProjectFields
            }
        }
    }
`;

export const DELETE_PROJECT = gql`
    mutation DeleteProject($id: UUID!) {
        deleteProject(id: $id) {
            success
        }
    }
`;

export const UPLOAD_PROJECT_KNOWLEDGE_FILE = gql`
    mutation UploadProjectKnowledgeFile($projectId: UUID!, $file: Upload!) {
        uploadProjectKnowledgeFile(projectId: $projectId, file: $file) {
            knowledgeFile {
                id
                fileName
            }
        }
    }
`;

export const DELETE_PROJECT_KNOWLEDGE_FILE = gql`
    mutation DeleteProjectKnowledgeFile($id: UUID!) {
        deleteProjectKnowledgeFile(id: $id) {
            success
        }
    }
`;

export const GET_WORKI_RECAP = gql`
    query WorkiRecap {
        workiRecap {
            questionCount
            lastQuestions {
                content
                createdAt
            }
            recentConversations {
                title
                updatedAt
            }
        }
    }
`;
